import { useEffect, useRef, useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { fmtMoney, fmtBytes } from "@/lib/format";
import { planGroups, scanLabel, ocrLabel, PLAN_RANK, annualSavingsPct, planHasYearly } from "@/lib/planPresentation";
import { track } from "@/lib/analytics";
import { Check, CircleNotch, Crown, ArrowRight, Vault, Scan, PenNib, SealCheck, Sparkle, ShieldCheck } from "@phosphor-icons/react";

const CAT_ICON = { STORE: Vault, SCAN: Scan, SIGN: PenNib, VERIFY: SealCheck, AUTOMATE: Sparkle, PROTECT: ShieldCheck };
const CAT_LABEL = { STORE: "Store", SCAN: "Scan", SIGN: "Sign", VERIFY: "Verify", AUTOMATE: "Automate", PROTECT: "Protect" };

// Staircase metadata. Levels 01..06 rise left→right on desktop via lg margin-top.
const TIER = {
  free: { level: "01", mt: "lg:mt-16", badge: null },
  basic: { level: "02", mt: "lg:mt-12", badge: null },
  personal: { level: "03", mt: "lg:mt-8", badge: "MOST POPULAR" },
  family: { level: "04", mt: "lg:mt-6", badge: null },
  premium: { level: "05", mt: "lg:mt-3", badge: null },
  business: { level: "06", mt: "lg:mt-0", badge: "COMPLETE DOCUMENT OPERATIONS" },
};

function topCapabilities(plan) {
  const flat = planGroups(plan).flatMap((g) => g.items);
  return flat.slice(-3); // most advanced capabilities of the tier
}

function TierCard({ plan, selected, onOpen, mt, dark, cta, interval, mismatch }) {
  const id = plan.plan_id;
  const isFree = id === "free";
  const featured = id === "personal";
  const business = id === "business";
  const level = TIER[id]?.level || "00";
  const badge = TIER[id]?.badge;
  const yearly = interval === "yearly" && planHasYearly(plan);
  const price = yearly ? plan.amount_yearly : plan.amount_monthly;
  const savings = yearly ? annualSavingsPct(plan) : null;
  const showPaid = isFree || plan.purchasable || mismatch;
  const caps = topCapabilities(plan);

  const base = dark
    ? "border-white/10 bg-white/[0.03] backdrop-blur-xl"
    : "border-slate-200 bg-white";
  const featuredCls = featured
    ? "border-primary/50 bg-primary/[0.06] shadow-[0_0_24px_rgba(212,175,55,0.12)]"
    : "";
  const businessCls = business
    ? "border-amber-200/30 bg-gradient-to-b from-white/[0.06] to-transparent"
    : "";
  const selectedCls = selected
    ? "ring-2 ring-primary/70 border-primary"
    : "";
  const muted = dark ? "text-white/55" : "text-slate-500";
  const capText = dark ? "text-white/80" : "text-slate-600";

  return (
    <div className={`${mt} lg:self-start`}>
      <div
        onClick={onOpen}
        data-testid={`tier-card-${id}`}
        className={`group relative w-full text-left rounded-2xl border p-5 flex flex-col min-h-[360px] cursor-pointer
          transition-[transform,border-color,box-shadow] duration-200 ease-[cubic-bezier(0.16,1,0.3,1)]
          motion-safe:hover:-translate-y-1
          ${base} ${featuredCls} ${businessCls} ${selectedCls}`}
      >
        {/* Header: level tag + (stacked) designation badge */}
        <span className={`font-mono text-[10px] tracking-[0.25em] ${featured ? "text-primary" : muted}`}>LEVEL {level}</span>
        <div className="mt-1.5 min-h-[22px]">
          {badge && (
            <span
              data-testid={`tier-badge-${id}`}
              className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[9px] font-semibold uppercase tracking-wider leading-tight ${
                featured
                  ? "bg-primary/15 text-primary border border-primary/40"
                  : dark
                  ? "bg-slate-700/70 text-slate-200 border border-slate-500/40"
                  : "bg-slate-100 text-slate-600 border border-slate-300"
              }`}
            >
              {featured && <Crown size={11} weight="fill" className="shrink-0" />} {badge}
            </span>
          )}
        </div>

        {/* Plan name */}
        <h3 className="mt-2 font-display text-xl font-bold tracking-wide">{plan.name}</h3>

        {/* Price */}
        <div className="mt-2 flex items-end gap-1 min-h-[38px]">
          {isFree ? (
            <span className="text-3xl font-extrabold font-display">Free</span>
          ) : showPaid ? (
            <>
              <span className="text-3xl font-extrabold font-display" data-testid={`plan-price-${id}`}>{fmtMoney(price, plan.currency)}</span>
              <span className={`text-sm mb-1 ${muted}`}>{yearly ? "/yr" : "/mo"}</span>
            </>
          ) : plan.local_pricing && price > 0 ? (
            <div className="flex flex-col leading-tight">
              <span className={`text-lg font-semibold font-display ${muted}`} data-testid={`plan-comingsoon-${id}`}>Coming soon</span>
              <span className={`text-xs ${muted}`} data-testid={`plan-est-price-${id}`}>≈ {fmtMoney(price, plan.currency)}{yearly ? "/yr" : "/mo"} in your region</span>
            </div>
          ) : (
            <span className={`text-lg font-semibold font-display ${muted}`} data-testid={`plan-comingsoon-${id}`}>Coming soon</span>
          )}
        </div>

        {yearly && savings ? (
          <span className="mt-1 inline-flex w-fit items-center rounded-full bg-primary/15 text-primary text-[11px] font-semibold px-2 py-0.5" data-testid={`plan-savings-${id}`}>Save {savings}%</span>
        ) : null}

        {/* Storage pill */}
        <div className="mt-3">
          <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium ${dark ? "bg-white/5 text-white/80 border border-white/10" : "bg-slate-100 text-slate-700"}`}>
            <Vault size={13} weight="fill" className="text-primary" /> {fmtBytes(plan.storage_bytes)} storage
          </span>
        </div>

        {/* Tagline */}
        {plan.tagline && <p className={`mt-3 text-sm ${muted}`}>{plan.tagline}</p>}

        {/* Top capabilities */}
        <ul className="mt-3 space-y-1.5 flex-1">
          {caps.map((it) => (
            <li key={it.cap} className={`flex items-start gap-2 text-sm ${capText}`}>
              <Check size={15} weight="bold" className="text-primary mt-0.5 shrink-0" />
              <span>{it.label}</span>
            </li>
          ))}
        </ul>

        {/* CTA */}
        <div className="mt-5" onClick={(e) => e.stopPropagation()}>
          {cta(plan)}
        </div>

        {/* "View details" affordance */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); onOpen(); }}
          aria-pressed={selected}
          aria-label={`View full features of the ${plan.name} plan (Level ${level})`}
          data-testid={`tier-select-btn-${id}`}
          className={`mt-2 inline-flex items-center gap-1 rounded outline-none focus-visible:ring-2 focus-visible:ring-primary/70 text-[11px] font-medium ${featured ? "text-primary" : muted} hover:text-primary transition-colors`}
        >
          {selected ? "Viewing full features" : "View full features"} <ArrowRight size={12} />
        </button>
      </div>
    </div>
  );
}

function DetailPanel({ plan, dark, cta }) {
  const groups = planGroups(plan);
  const muted = dark ? "text-white/55" : "text-slate-500";
  const famCard = dark ? "border-white/10 bg-white/[0.03]" : "border-slate-200 bg-white";
  const itemText = dark ? "text-white/80" : "text-slate-600";

  return (
    <div
      role="region"
      aria-label={`${plan.name} plan — full features`}
      data-testid={`capability-panel-${plan.plan_id}`}
      className={`rounded-2xl border p-6 sm:p-8 ${dark ? "border-primary/20 bg-white/[0.02] backdrop-blur-xl" : "border-slate-200 bg-slate-50"}`}
    >
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <span className="font-mono text-[10px] tracking-[0.25em] text-primary">LEVEL {TIER[plan.plan_id]?.level} · {plan.name.toUpperCase()}</span>
          <h3 className="font-display text-2xl font-bold mt-1">{plan.tagline || `Your ${plan.name} vault`}</h3>
          <p className={`text-sm mt-1 ${muted}`}>{fmtBytes(plan.storage_bytes)} storage · {scanLabel(plan.scans_per_month)} · {ocrLabel(plan.ocr_per_month)}</p>
        </div>
        <div className="shrink-0">{cta(plan)}</div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {groups.map((g) => {
          const Icon = CAT_ICON[g.cat] || Check;
          return (
            <div key={g.cat} className={`rounded-xl border p-4 ${famCard}`}>
              <div className="flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wider text-primary">
                <Icon size={15} weight="bold" /> {CAT_LABEL[g.cat]}
              </div>
              <ul className="mt-2.5 space-y-2">
                {g.items.map((it) => (
                  <li key={it.cap} className={`flex items-start gap-2 text-sm ${itemText}`}>
                    <Check size={15} weight="bold" className="text-primary mt-0.5 shrink-0" />
                    <span>
                      {it.label}
                      {it.early && <span className="ml-1.5 rounded-full bg-primary/15 text-primary text-[10px] px-1.5 py-0.5 align-middle">Early access</span>}
                      {it.tip && <span className={`block text-xs mt-0.5 ${muted}`}>{it.tip}</span>}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// Shared futuristic pyramid pricing used by BOTH the public landing page and the
// authenticated dashboard. Drop-in replacement for PlanCards: same props + testids.
// Data + capabilities come solely from GET /api/plans via planPresentation.js.
export default function PyramidPricing({ plans, mode = "public", theme = "light", sub, busy, onFree, onSelect, onWaitlist, comingSoonLabel, interval = "monthly", mismatch = false }) {
  const dark = theme === "dark";
  const reduce = useReducedMotion();
  const ordered = (Array.isArray(plans) ? [...plans] : []).sort((a, b) => (PLAN_RANK[a.plan_id] ?? 99) - (PLAN_RANK[b.plan_id] ?? 99));
  const [selectedId, setSelectedId] = useState(null);
  const panelRef = useRef(null);

  useEffect(() => { track("pricing_view", { source: mode }); }, [mode]);

  // Default the detail panel to the featured plan once plans load.
  useEffect(() => {
    if (!selectedId && ordered.some((p) => p.plan_id === "personal")) setSelectedId("personal");
    else if (!selectedId && ordered.length) setSelectedId(ordered[0].plan_id);
  }, [plans]); // eslint-disable-line react-hooks/exhaustive-deps

  const openPlan = (id) => {
    setSelectedId(id);
    requestAnimationFrame(() => {
      panelRef.current?.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "nearest" });
    });
  };

  // CTA logic — identical behavior & data-testids to the previous PlanCards.
  const cta = (plan) => {
    const id = plan.plan_id;
    const isFree = id === "free";
    const featured = id === "personal";
    const primary = `rounded-full w-full ${featured ? "bg-primary text-[#2a2d31] font-semibold hover:bg-primary/90" : dark ? "bg-white/10 hover:bg-white/20 text-white" : ""}`;
    if (mode === "public") {
      if (isFree) return <Button onClick={onFree} className={primary} data-testid={`plan-cta-${id}`}>Start Free</Button>;
      if (plan.purchasable) return <Button onClick={() => { track("plan_selected", { plan_id: id, source: "public" }); onSelect(plan); }} className={primary} data-testid={`plan-cta-${id}`}>Choose Plan</Button>;
      return <Button onClick={onWaitlist} variant="outline" className="rounded-full w-full border-primary/40 text-primary hover:bg-primary/10" data-testid={`plan-waitlist-${id}`}>Join waitlist</Button>;
    }
    const current = id === sub?.plan_id;
    if (current) return <Button disabled variant="outline" className="rounded-full w-full" data-testid={`plan-current-${id}`}>Current Plan</Button>;
    if (isFree) return <Button disabled variant="ghost" className="rounded-full w-full" data-testid={`plan-free-${id}`}>Free tier</Button>;
    if (mismatch) return <Button disabled variant="outline" className="rounded-full w-full" data-testid={`plan-comingsoon-cta-${id}`}>{comingSoonLabel || "Switch country to buy"}</Button>;
    if (!plan.purchasable) return <Button disabled variant="outline" className="rounded-full w-full" data-testid={`plan-comingsoon-cta-${id}`}>{comingSoonLabel || "Coming soon"}</Button>;
    const isUpgrade = PLAN_RANK[id] > PLAN_RANK[sub?.plan_id ?? "free"];
    return (
      <Button onClick={() => { track("plan_selected", { plan_id: id, source: "auth" }); track("checkout_started", { plan_id: id }); onSelect(plan); }} disabled={busy === id} className="rounded-full w-full" data-testid={`plan-${isUpgrade ? "upgrade" : "change"}-${id}`}>
        {busy === id ? <CircleNotch className="animate-spin" /> : isUpgrade ? "Upgrade" : "Change Plan"}
      </Button>
    );
  };

  const selectedPlan = ordered.find((p) => p.plan_id === selectedId);

  return (
    <div data-testid="pricing-pyramid-container">
      <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 lg:items-start lg:pt-4" data-testid="plan-cards-grid">
        {ordered.map((p) => (
          <TierCard
            key={p.plan_id}
            plan={p}
            selected={selectedId === p.plan_id}
            onOpen={() => openPlan(p.plan_id)}
            mt={TIER[p.plan_id]?.mt || ""}
            dark={dark}
            cta={cta}
            interval={interval}
            mismatch={mismatch}
          />
        ))}
      </div>

      <div ref={panelRef} className="mt-6 scroll-mt-24">
        <AnimatePresence mode="wait">
          {selectedPlan && (
            <motion.div
              key={selectedPlan.plan_id}
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={reduce ? { opacity: 0 } : { opacity: 0, y: -8 }}
              transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
            >
              <DetailPanel plan={selectedPlan} dark={dark} cta={cta} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
